import React from 'react';
import styled from 'styled-components';
import { State } from 'model/State';
import { useSelector } from 'react-redux';
import { FormattedDate, FormattedMessage } from 'react-intl';
import ChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';

import { Dimensions } from 'components/visuals';
import { removeTimezone } from '../common/utils';
import { selectDetails, selectGetCustomerByEmail, selectSwitchedUser } from '../duck/selectors';
import {
  ExpiredDateWrapper,
  MigratedWrapper,
  SoonExpiredDateWrapper,
} from './LicensesCollapsedRow';
import { isInNext30Days, isPastEndDate } from 'util/mac/helpers';

const Cell = styled.td<{ width: number }>`
  font-size: 14px;
  align-self: center;
  width: ${({ width }) => width}%;
  padding: ${Dimensions.units(1)}px ${Dimensions.units(0.25)}px;
  overflow-wrap: anywhere;

  && span {
    line-height: unset;
  }
`;

const ChevronCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ExpirationDateComponent = ({ date, entSubMigrated, isExpired, expiresIn30Days }) => {
  const expirationDateMessage = (
    <FormattedDate value={removeTimezone(date)} year="numeric" month="short" day="2-digit" />
  );

  if (entSubMigrated) {
    return <></>;
  }

  if (isExpired) {
    return (
      <ExpiredDateWrapper data-cy="expiration-date-red">{expirationDateMessage}</ExpiredDateWrapper>
    );
  }

  if (expiresIn30Days) {
    return (
      <SoonExpiredDateWrapper data-cy="expiration-date-green">
        {expirationDateMessage}
      </SoonExpiredDateWrapper>
    );
  }

  return expirationDateMessage;
};

const MainContact = ({ entSubMigrated, mainContactEmail, isDataCenter }) => {
  if (entSubMigrated) {
    return (
      <MigratedWrapper>
        <FormattedMessage
          defaultMessage={isDataCenter ? 'Migrated' : 'Site Migrated'}
          id={isDataCenter ? 'mac.licenses.migrated' : 'mac.licenses.site-migrated'}
        />
      </MigratedWrapper>
    );
  }
  return mainContactEmail;
};

export const EndUserLicensesCollapsedRow = ({ showContent, rowItem, isTrial }) => {
  const {
    accountId,
    licenseId,
    expireDate,
    productDescription,
    accountName,
    mainContactEmail,
    lastTransactionDate,
    entSubMigrated,
    partnerId,
  } = rowItem;
  const userEmail = useSelector(selectSwitchedUser);
  const customerByEmail = useSelector((state) =>
    selectGetCustomerByEmail(state as State)?.[userEmail]?.ifSuccess((r) => r),
  );
  const detailsData = useSelector((state) => selectDetails(state as State, accountId));
  const isPartnerActive = partnerId === +customerByEmail?.organisationDetails.id;
  const isExpired = isPastEndDate(expireDate);
  const expiresIn30Days = isInNext30Days(expireDate);
  const isDataCenter = detailsData?.merchandise?.productPackage?.isDataCenter === true;

  return (
    <>
      <Cell width={15} key="licenses-row-sen">
        <ChevronCellWrapper>
          {showContent ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
          {isTrial && <EmojiFrequentIcon label="" />}
          SEN-{accountId || `L${licenseId}`}
        </ChevronCellWrapper>
      </Cell>
      <Cell width={15} key="licenses-row-product">
        {productDescription}
      </Cell>
      <Cell width={15} key="licenses-row-site-name">
        {accountName}
      </Cell>
      <Cell width={20} key="licenses-main-contact">
        {isPartnerActive ? (
          <MainContact
            entSubMigrated={entSubMigrated}
            mainContactEmail={mainContactEmail}
            isDataCenter={isDataCenter}
          />
        ) : (
          'N/A'
        )}
      </Cell>
      <Cell width={15} key="licenses-end-date">
        {isPartnerActive ? (
          <ExpirationDateComponent
            date={expireDate}
            entSubMigrated={entSubMigrated}
            isExpired={isExpired}
            expiresIn30Days={expiresIn30Days}
          />
        ) : (
          'N/A'
        )}
      </Cell>
      <Cell width={10} key="licenses-last-transaction">
        {isPartnerActive ? (
          <ExpirationDateComponent
            date={lastTransactionDate}
            entSubMigrated={entSubMigrated}
            isExpired={isExpired}
            expiresIn30Days={expiresIn30Days}
          />
        ) : (
          'N/A'
        )}
      </Cell>
    </>
  );
};
